/**
 * The figures a self-serve customer sees about their own plan. Pure, so the
 * money maths is tested directly.
 *
 * Two sources:
 * - the stored terms (GET .../plan): rate, contract value, what was paid;
 * - usage against them (GET .../plan/usage, avrio-backend#178): hires used and
 *   remaining, amount billed, balance, overage. A hire counts when the offer is
 *   made (Nachi, 2026-10-01).
 *
 * "Hires included" comes from usage when it has loaded, otherwise from
 * contract value / rate per hire (how the plan was priced). Either way it is
 * null rather than rounded when the two do not divide evenly, because a
 * rounded hire count would be a number nobody agreed to.
 *
 * The balance comes only from usage. Contract value minus the down payment
 * would overstate it as soon as an offer is billed, so without usage there is
 * no balance to show.
 */
import { CustomerPlan, CustomerPlanUsage } from '../../types';

export interface PlanSummary {
  hiresIncluded: number | null;
  ratePerHire: number | null;
  contractValue: number | null;
  paid: number | null;
  currency: string;
}

export interface UsageSummary {
  hiresUsed: number | null;
  /** Never negative: hires past the plan are in hiresOver instead. */
  hiresRemaining: number | null;
  hiresOver: number;
  amountBilled: number | null;
  balanceRemaining: number | null;
  overageAmount: number;
}

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

export const summarisePlan = (
  plan: CustomerPlan,
  usage?: CustomerPlanUsage | null,
): PlanSummary => {
  const ratePerHire = num(plan.ratePerHire);
  const contractValue = num(plan.contractValue);

  let hiresIncluded: number | null = null;
  if (usage) {
    hiresIncluded = num(usage.hiresIncluded);
  } else if (ratePerHire !== null && ratePerHire > 0 && contractValue !== null) {
    const hires = contractValue / ratePerHire;
    hiresIncluded = Number.isInteger(hires) ? hires : null;
  }

  return {
    hiresIncluded,
    ratePerHire,
    contractValue,
    paid: num(plan.downPayment),
    currency: plan.currency ?? usage?.currency ?? 'USD',
  };
};

export const summariseUsage = (usage: CustomerPlanUsage): UsageSummary => {
  const remaining = num(usage.hiresRemaining);
  return {
    hiresUsed: num(usage.hiresUsed),
    hiresRemaining: remaining === null ? null : Math.max(remaining, 0),
    hiresOver: remaining !== null && remaining < 0 ? -remaining : 0,
    amountBilled: num(usage.amountBilled),
    balanceRemaining: num(usage.balanceRemaining),
    overageAmount: Math.max(num(usage.overageAmount) ?? 0, 0),
  };
};
